/**
 * «Libreta de direcciones» (#159) — the company's counterparties, as the create forms suggest them.
 *
 * The server merges two sources: the entries someone saved («Guardada»), and the counterparties that
 * the documents that stand carry («De documentos»), with when each was last used. A saved entry
 * stands in for the documents' row for its NIF, so saving one is also how a name misspelt on old
 * documents is put right for the next ones. The list keeps every entry in reach, so the office sees a
 * Gestor's entry beside the company's for the same NIF and can correct either; only the suggestions
 * keep one per NIF. A suggestion that should not be offered at all is hidden instead, and the
 * «Ocultas» view lists those the documents would otherwise suggest, so they can be shown again. The
 * documents themselves never change.
 *
 * **Whose book** is the server's answer, never this screen's: a Gestor Individual reads their own,
 * everyone else the whole company's, a Gestor's entries included and marked as theirs. A new entry
 * or hide lands in the caller's own book; an edit, a removal or a show-again acts on whatever is in
 * reach, and an entry keeps its owner.
 *
 * **Who may change it** is `writeDecas`. ORG_VIEWER and the platform roles read only. The server
 * refuses a write to ORG_VIEWER and SUPPORT, but SUPER_ADMIN holds `deca:write` there, so for it this
 * screen's check is the only one that keeps a write out of a customer's book (spec §2). A platform
 * role reads the company chosen in «Empresa», and with none chosen there is no company to read: the
 * screen says so rather than show ASGTRANS's own empty book as if it were somebody's.
 *
 * **One tab per kind** (#221): Empresas, Lugares, Vehículos, Remolques and Conductores, each with its
 * own columns and its own form, the tab in the URL as `tipo`. Places and plates come from the
 * documents as parties do, and are hidden the same way, by their value. Drivers are only ever what
 * the company saved, so their tab has no «Ocultas». A party's requests are #159's, kind and all left
 * out, since a party is what the server reads when no kind is named.
 *
 * Modelled on `ExternalDecasScreen`: page, search and view in the URL, and plain
 * `try/catch/finally` writes with local state, each followed by an invalidation that reaches the
 * suggestions as well as this list.
 */

import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { unwrap } from '@deca/api'

import {
  BOOK_TABS,
  entryInputOf,
  kindOfTipo,
  labelOf,
  type AddressBookEntry,
  type AddressBookItem,
  type AddressBookKind,
  type BookTipo,
} from '../app/addressBook.ts'
import { api } from '../app/api.ts'
import { addressBookErrorMessage, apiErrorMessage } from '../app/errors.ts'
import { invalidateAddressBook, useAddressBook, useDeadAddresses } from '../app/queries.ts'
import { isEmailAddress } from '../deca/emailAddress.ts'
import { EmailTagsInput } from '../ui/EmailTagsInput.tsx'
import { SettingBox } from '../ui/SettingBox.tsx'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDate } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, Chip, EmptyState, NoAccessState, Notice } from '../ui/primitives.tsx'
import { DataTable, Pagination, type Column } from '../ui/DataTable.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { Icon } from '../ui/Icon.tsx'
import { TextAreaInput, TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { useStepBackPastEnd } from '../ui/useStepBackPastEnd.ts'
import { addressBookSearchSchema, PAGE_DEFAULT, PER_PAGE } from './searchSchemas.ts'

/** The entry form's fields, as typed: the kind's own are shown, the rest stay empty. */
interface EntryValues {
  name: string
  nif: string
  address: string
  plate: string
  /** «Destinatarios del DeCA» and «Enviar automáticamente» (#219), a party's alone. */
  decaRecipients: string[]
  decaAutoSend: boolean
}

/** What the entry form is open for: a new entry, one saved from the documents, or a correction. */
interface EntryDraft {
  mode: 'add' | 'save' | 'edit'
  /** What the entry is, which its tab decides and an edit keeps. */
  kind: AddressBookKind
  /** The entry being corrected; null for the other two, which create one. */
  entryId: string | null
  values: EntryValues
}

const NO_VALUES: EntryValues = { name: '', nif: '', address: '', plate: '', decaRecipients: [], decaAutoSend: false }

const valuesOf = (row: AddressBookItem): EntryValues => ({
  name: row.name ?? '',
  nif: row.nif ?? '',
  address: row.address ?? '',
  plate: row.plate ?? '',
  decaRecipients: [...row.decaRecipients],
  decaAutoSend: row.decaAutoSend,
})

/**
 * A saved entry by its id; a row from the documents by what the server keeps it unique on within
 * the tab: a party's NIF, a place's address, a plate.
 */
const rowKeyOf = (row: AddressBookItem) => row.entryId ?? `${row.kind}:${row.nif ?? row.plate ?? row.address}`

/** What a row from the documents is hidden by: the value the server keys it on. */
const hiddenValueOf = (row: AddressBookItem): string => (row.kind === 'PLACE' ? row.address : row.plate) ?? ''

export function AddressBookScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const { organisation: context } = useOrganisationChoice()
  // Without a company chosen, a platform role's requests carry no `X-Organisation-Id` and would
  // read ASGTRANS's own book, which is no customer's: nothing is asked, and the screen says why.
  const needsCompany = abilities.choosesOrganisation && !context

  // Page, search and view live in the URL, never in component state, so a filtered list is a link.
  const search = addressBookSearchSchema.parse(useSearch({ strict: false }))
  const kind = kindOfTipo(search.tipo)
  // A driver is only ever saved, never suggested from the documents, so it has nothing hidden.
  const hidden = search.ocultas === true && kind !== 'DRIVER'
  const list = useAddressBook(
    {
      page: search.page ?? PAGE_DEFAULT,
      perPage: PER_PAGE,
      ...(search.q ? { q: search.q } : {}),
      ...(hidden ? { hidden: true } : {}),
      ...(kind !== 'PARTY' ? { kind } : {}),
    },
    abilities.readsDecas && !needsCompany,
  )
  /** A tab's own wording, where it has one; a party's is the book's, as #159 wrote it. */
  const copy = (key: 'listTitle' | 'searchLabel' | 'dialogHelp' | 'emptyTitle' | 'emptyBody') =>
    kind === 'PARTY' ? t(`addressBook.${key}`) : t(`addressBook.kinds.${kind}.${key}`)

  // A page past the end — its last row removed or hidden, or an old link — shows the last page
  // rather than an empty book. `replace`, so Back does not return to the empty page (#159, #165).
  useStepBackPastEnd(list.data?.meta, search.page ?? PAGE_DEFAULT, (page) => {
    void navigate({ to: '/libreta', search: { ...search, page }, replace: true })
  })

  const [draft, setDraft] = useState<EntryDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** The row a write is running on, whose buttons wait for it. */
  const [busyRow, setBusyRow] = useState<string | null>(null)

  // Every hook above the early returns — the rule this portal keeps, because a conditional return
  // placed one line higher changes the hook order on the render where the principal arrives.
  if (!abilities.readsDecas) {
    return (
      <AppShell title={t('addressBook.title')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  if (needsCompany) {
    return (
      <AppShell title={t('addressBook.title')} subtitle={t('addressBook.subtitle')}>
        <Notice tone="info" title={t('addressBook.chooseCompanyTitle')}>
          {t('addressBook.chooseCompany')}
        </Notice>
      </AppShell>
    )
  }

  /**
   * Another search, view or tab starts again on its first page, as every new filter does. The tab
   * is kept unless another is chosen, and another tab starts with no search and the book shown.
   */
  const goTo = (next: { q?: string; ocultas?: boolean; tipo?: BookTipo }) =>
    void navigate({ to: '/libreta', search: { q: next.q, page: 1, ocultas: next.ocultas, tipo: next.tipo } })
  const tipo = search.tipo

  async function write(row: AddressBookItem, request: () => Promise<unknown>, done: string) {
    setBusyRow(rowKeyOf(row))
    setError(null)
    try {
      await request()
      invalidateAddressBook(client)
      toast.success(done)
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusyRow(null)
    }
  }

  function remove(row: AddressBookItem, entryId: string) {
    // The documents are untouched, so a counterparty they carry comes back as a suggestion from
    // them: the confirmation says so, or the row reappearing would look like the delete failed.
    // A driver is never suggested from the documents, so removing one removes it altogether.
    const question = row.kind === 'DRIVER' ? 'addressBook.kinds.DRIVER.deleteConfirm' : 'addressBook.deleteConfirm'
    if (!window.confirm(t(question, { name: labelOf(row) }))) return
    void write(
      row,
      () => unwrap(api.DELETE('/api/v1/address-book/entries/{entryId}', { params: { path: { entryId } } })),
      t('addressBook.deleted', { name: labelOf(row) }),
    )
  }

  // A party by the NIF as the row carries it, as #159 did; a place or a plate by its value (#221),
  // since an address is longer than a path segment may be. The server compares both normalised. A
  // driver never comes from the documents, so it is never offered to hide and asks nothing.
  const hide = (row: AddressBookItem) =>
    void write(
      row,
      () =>
        row.kind === 'DRIVER'
          ? Promise.resolve()
          : row.kind === 'PARTY'
            ? unwrap(api.PUT('/api/v1/address-book/hidden/{nif}', { params: { path: { nif: row.nif ?? '' } } }))
            : unwrap(api.PUT('/api/v1/address-book/hidden', { params: { query: { kind: row.kind, value: hiddenValueOf(row) } } })),
      t('addressBook.hiddenDone', { name: labelOf(row) }),
    )

  const unhide = (row: AddressBookItem) =>
    void write(
      row,
      () =>
        row.kind === 'DRIVER'
          ? Promise.resolve()
          : row.kind === 'PARTY'
            ? unwrap(api.DELETE('/api/v1/address-book/hidden/{nif}', { params: { path: { nif: row.nif ?? '' } } }))
            : unwrap(
                api.DELETE('/api/v1/address-book/hidden', { params: { query: { kind: row.kind, value: hiddenValueOf(row) } } }),
              ),
      t('addressBook.shownDone', { name: labelOf(row) }),
    )

  function saved(entry: AddressBookEntry, mode: EntryDraft['mode']) {
    setDraft(null)
    invalidateAddressBook(client)
    toast.success(t(mode === 'edit' ? 'addressBook.updated' : 'addressBook.saved', { name: labelOf(entry) }))
  }

  /** Which buttons a row offers. A hide is about the documents' suggestion, so an entry has none. */
  function actionsFor(row: AddressBookItem) {
    const busy = busyRow === rowKeyOf(row)
    if (hidden) {
      return (
        <div className="table__actions">
          <Button size="sm" variant="ghost" icon="eye" disabled={busy} onClick={() => unhide(row)}>
            {t('addressBook.show')}
          </Button>
        </div>
      )
    }
    const entryId = row.source === 'ENTRY' ? row.entryId : null
    if (entryId) {
      return (
        <div className="table__actions">
          <Button
            size="sm"
            variant="ghost"
            icon="edit"
            disabled={busy}
            onClick={() => setDraft({ mode: 'edit', kind: row.kind, entryId, values: valuesOf(row) })}
          >
            {t('common.edit')}
          </Button>
          <Button size="sm" variant="ghost" icon="trash" disabled={busy} onClick={() => remove(row, entryId)}>
            {t('common.delete')}
          </Button>
        </div>
      )
    }
    return (
      <div className="table__actions">
        <Button
          size="sm"
          variant="ghost"
          icon="book"
          disabled={busy}
          onClick={() => setDraft({ mode: 'save', kind: row.kind, entryId: null, values: valuesOf(row) })}
        >
          {t('common.save')}
        </Button>
        <Button size="sm" variant="ghost" icon="ban" disabled={busy} onClick={() => hide(row)}>
          {t('addressBook.hide')}
        </Button>
      </div>
    )
  }

  /** The first column's cell: what the row is, where it comes from, and whose own entry it is. */
  const lead = (row: AddressBookItem, text: string | null) => (
    <div className="address-book__party">
      <span className="address-book__name">{text}</span>
      {/* Every hidden row comes from the documents, and every driver from the book: the view
          already says which. */}
      {hidden || row.kind === 'DRIVER' ? null : <SourceTag source={row.source} />}
      {/* Whose own entry this is — for the office, which sees its Gestores' entries beside
          the company's. A Gestor's book holds only their own, so it would only repeat them. */}
      {row.owner && !abilities.ownDocumentsOnly ? (
        <span className="small muted address-book__owner">{t('addressBook.owner', { owner: row.owner })}</span>
      ) : null}
    </div>
  )

  const lastUsed: Column<AddressBookItem> = {
    key: 'lastUsed',
    header: t('addressBook.columns.lastUsed'),
    numeric: true,
    render: (row) => (row.lastUsedAt ? formatDate(row.lastUsedAt, i18n.language) : null),
  }

  /** Each kind's columns, its own fields first. */
  const fields: Record<AddressBookKind, Column<AddressBookItem>[]> = {
    PARTY: [
      { key: 'name', header: t('addressBook.columns.name'), render: (row) => lead(row, row.name) },
      { key: 'nif', header: t('addressBook.columns.nif'), render: (row) => <span className="tabular">{row.nif}</span> },
      { key: 'address', header: t('addressBook.columns.address'), render: (row) => row.address },
      lastUsed,
    ],
    PLACE: [
      { key: 'address', header: t('addressBook.columns.place'), render: (row) => lead(row, row.address) },
      { key: 'name', header: t('addressBook.columns.shortName'), render: (row) => row.name },
      lastUsed,
    ],
    VEHICLE: [
      { key: 'plate', header: t('addressBook.columns.plate'), render: (row) => lead(row, row.plate) },
      lastUsed,
    ],
    TRAILER: [
      { key: 'plate', header: t('addressBook.columns.plate'), render: (row) => lead(row, row.plate) },
      lastUsed,
    ],
    DRIVER: [
      { key: 'name', header: t('addressBook.columns.driverName'), render: (row) => lead(row, row.name) },
      { key: 'nif', header: t('addressBook.columns.dni'), render: (row) => <span className="tabular">{row.nif}</span> },
    ],
  }

  const columns: Column<AddressBookItem>[] = [
    ...fields[kind],
    ...(abilities.writeDecas
      ? [
          {
            key: 'actions',
            header: t('common.actions'),
            align: 'right',
            render: actionsFor,
          } satisfies Column<AddressBookItem>,
        ]
      : []),
  ]

  const title = hidden ? t('addressBook.hiddenTitle') : copy('listTitle')

  return (
    <AppShell title={t('addressBook.title')} subtitle={t('addressBook.subtitle')}>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="filter-row">
        <div className="segmented" role="group" aria-label={t('addressBook.kinds.label')}>
          {BOOK_TABS.map((tab) => (
            <button
              key={tab.tipo}
              type="button"
              className="segmented__option"
              aria-pressed={kind === tab.kind}
              onClick={() => goTo({ tipo: tab.tipo === 'empresas' ? undefined : tab.tipo })}
            >
              {t(`addressBook.kinds.${tab.kind}.tab`)}
            </button>
          ))}
        </div>
        {kind === 'DRIVER' ? null : (
          <div className="segmented" role="group" aria-label={t('addressBook.viewLabel')}>
            <button
              type="button"
              className="segmented__option"
              aria-pressed={!hidden}
              onClick={() => goTo({ q: search.q, tipo })}
            >
              {t('addressBook.viewBook')}
            </button>
            <button
              type="button"
              className="segmented__option"
              aria-pressed={hidden}
              onClick={() => goTo({ q: search.q, ocultas: true, tipo })}
            >
              {t('addressBook.viewHidden')}
            </button>
          </div>
        )}
      </div>

      <Card
        title={title}
        subtitle={hidden ? t('addressBook.hiddenHelp') : undefined}
        flush
        actions={
          <>
            <form
              className="topbar__search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault()
                const value = new FormData(event.currentTarget).get('q')
                const q = typeof value === 'string' && value.trim() ? value.trim() : undefined
                goTo({ q, ocultas: hidden || undefined, tipo })
              }}
            >
              <Icon name="search" size={16} />
              <input
                className="input"
                type="search"
                name="q"
                // Remounted when the URL's search changes, Back included, so the box shows it.
                key={search.q ?? ''}
                defaultValue={search.q ?? ''}
                placeholder={copy('searchLabel')}
                aria-label={copy('searchLabel')}
              />
            </form>
            {abilities.writeDecas && !hidden ? (
              <Button
                variant="primary"
                icon="plus"
                onClick={() => setDraft({ mode: 'add', kind, entryId: null, values: NO_VALUES })}
              >
                {t('addressBook.add')}
              </Button>
            ) : null}
          </>
        }
      >
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={rowKeyOf}
          caption={title}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          // Each view's own empty state. A search that finds nothing keeps `DataTable`'s «Sin
          // resultados», which is then the true answer.
          empty={
            search.q ? undefined : hidden ? (
              <EmptyState
                icon="eye"
                title={t('addressBook.hiddenEmptyTitle')}
                body={t('addressBook.hiddenEmptyBody')}
              />
            ) : (
              <EmptyState icon="book" title={copy('emptyTitle')} body={copy('emptyBody')} />
            )
          }
        />
        {list.data?.meta ? (
          <Pagination
            page={list.data.meta.page}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            totalPages={list.data.meta.totalPages}
            onPage={(page) => void navigate({ to: '/libreta', search: { ...search, page } })}
          />
        ) : null}
      </Card>

      {/* Mounted per opening, so each opens on its own values. */}
      {draft ? (
        <EntryDialog draft={draft} help={copy('dialogHelp')} onClose={() => setDraft(null)} onSaved={saved} />
      ) : null}
    </AppShell>
  )
}

/** Where a row comes from: colour, icon and text, never colour alone (design spec §2). */
function SourceTag({ source }: { source: AddressBookItem['source'] }) {
  const { t } = useTranslation()
  return source === 'ENTRY' ? (
    <Chip tone="info" icon="book">
      {t('addressBook.source.ENTRY')}
    </Chip>
  ) : (
    <Chip tone="neutral" icon="file" outline>
      {t('addressBook.source.HISTORY')}
    </Chip>
  )
}

/**
 * The entry form: «Añadir», «Guardar» on a row from the documents (opened on its values), and
 * «Editar», with the fields of the entry's kind (#221). What a kind needs is required here as a hint;
 * the server's 400 decides, and a NIF or a plate with no letter or digit in it passes this check and
 * is refused there.
 *
 * A refusal stays in the dialog, beside the values it is about: the NIF the book already holds
 * (`ADDRESS_BOOK_ENTRY_EXISTS`), the company's own (`ADDRESS_BOOK_OWN_COMPANY`), an entry removed
 * meanwhile. Nothing typed is lost to it.
 */
function EntryDialog({
  draft,
  help,
  onClose,
  onSaved,
}: {
  draft: EntryDraft
  help: string
  onClose: () => void
  onSaved: (entry: AddressBookEntry, mode: EntryDraft['mode']) => void
}) {
  const { t } = useTranslation()
  const form = useForm<EntryValues>({ defaultValues: draft.values })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const required = (value: string) => value.trim() !== '' || t('errors.required')
  const recipients = form.watch('decaRecipients')
  const dead = useDeadAddresses(recipients)

  async function submit(values: EntryValues) {
    // The tags mark an address the server would not take; it would refuse the entry with it.
    if (values.decaRecipients.some((address) => !isEmailAddress(address))) {
      setError(t('emailTags.invalid'))
      return
    }
    setBusy(true)
    setError(null)
    const body = entryInputOf(draft.kind, values)
    try {
      const entry = draft.entryId
        ? await unwrap(
            api.PUT('/api/v1/address-book/entries/{entryId}', {
              params: { path: { entryId: draft.entryId } },
              body,
            }),
          )
        : await unwrap(api.POST('/api/v1/address-book/entries', { body }))
      onSaved(entry, draft.mode)
    } catch (caught) {
      setError(addressBookErrorMessage(caught, draft.kind, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      title={t(`addressBook.${draft.mode}Title`)}
      subtitle={help}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() => void form.handleSubmit(submit)()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {/* The contract's lengths, so an over-long value is stopped in the box, not refused later. */}
        {draft.kind === 'PARTY' ? (
          <>
            <TextInput
              label={t('addressBook.columns.name')}
              required
              maxLength={200}
              error={form.formState.errors.name?.message}
              {...form.register('name', { validate: required })}
            />
            <TextInput
              label={t('addressBook.columns.nif')}
              required
              maxLength={20}
              error={form.formState.errors.nif?.message}
              {...form.register('nif', { validate: required })}
            />
            <TextInput
              label={t('addressBook.columns.address')}
              optional
              maxLength={300}
              {...form.register('address')}
            />
            <EmailTagsInput
              label={t('addressBook.recipients')}
              optional
              help={t('addressBook.recipientsHelp')}
              value={recipients}
              dead={dead}
              onChange={(next) => form.setValue('decaRecipients', next, { shouldDirty: true })}
            />
            <SettingBox
              label={t('addressBook.autoSend')}
              help={t('addressBook.autoSendHelp')}
              checked={form.watch('decaAutoSend')}
              onChange={(checked) => form.setValue('decaAutoSend', checked, { shouldDirty: true })}
            />
          </>
        ) : null}
        {draft.kind === 'PLACE' ? (
          <>
            <TextAreaInput
              label={t('addressBook.columns.place')}
              required
              maxLength={300}
              rows={2}
              error={form.formState.errors.address?.message}
              {...form.register('address', { validate: required })}
            />
            <TextInput
              label={t('addressBook.columns.shortName')}
              optional
              maxLength={200}
              {...form.register('name')}
            />
          </>
        ) : null}
        {draft.kind === 'VEHICLE' || draft.kind === 'TRAILER' ? (
          <TextInput
            label={t('addressBook.columns.plate')}
            required
            maxLength={20}
            error={form.formState.errors.plate?.message}
            {...form.register('plate', { validate: required })}
          />
        ) : null}
        {draft.kind === 'DRIVER' ? (
          <>
            <TextInput
              label={t('addressBook.columns.driverName')}
              required
              maxLength={200}
              error={form.formState.errors.name?.message}
              {...form.register('name', { validate: required })}
            />
            <TextInput
              label={t('addressBook.columns.dni')}
              required
              maxLength={20}
              error={form.formState.errors.nif?.message}
              {...form.register('nif', { validate: required })}
            />
          </>
        ) : null}
      </div>
    </Dialog>
  )
}
